import { useGuideEnabled } from "../lib/guide-preference";
import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { Bookmark, Check, ChevronDown, ChevronLeft, ChevronRight, CircleHelp, Columns2, CommandIcon, ExternalLink, GitBranch, Info, ListOrdered, LocateFixed, MessageSquare, MessageSquareText, PanelLeftClose, Search, Trash2, UserRound, WrapText, X } from "lucide-react";
import {
  buildTourStops,
  resolveGuideItem,
  type CompiledRegion,
  type CodeMapCompilation,
  type TourStop,
} from "../../shared/codemap";
import type { GuideLayer, GuidePrompt, ReviewAnnotation, ReviewGuide, ReviewPresentation } from "../../shared/guide";
import { fileOrderModes, type FileOrderMode } from "../../shared/config";
import type { LayerStatus, RegionStatus } from "../../shared/progress";
import { draftAnchorSpan, draftCommentKind, draftCommentKinds, lineAnchorForDraft, lineDraftItemId, type DraftAnchor, type DraftComment, type DraftCommentKind, type ReviewLayerViewState } from "../lib/db";
import { bookmarkFreshness, bookmarkId, bookmarkStorageKey, lineTextAt, loadBookmarks, saveBookmarks, type ReviewBookmark } from "../lib/bookmarks";
import { initialCollapsedFiles } from "../lib/file-collapse";
import { fileContentFingerprint, loadViewedFiles, saveViewedFiles, viewedFileState, viewedStorageKey, type ViewedFileState } from "../lib/viewed-files";
import type { FixtureDescriptor, FixtureFile, FixtureFiles } from "../lib/fixtures";
import type { HostedConversationComment, HostedPreview, HostedReviewContext, HostedReviewThread } from "../lib/hosted";
import { useLazySerializer } from "../hooks/useLazySerializer";
import { BinaryFilePreview } from "./BinaryFilePreview";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { FileChatContextInput } from "../lib/file-chat";
import { orderStepPaths } from "../lib/file-order";
import { checksSignal } from "../lib/pull-signals";
import { buildUnifiedDiff, diffStats } from "../lib/unified-diff";
import type { ChewCodeTarget } from "./ChewMarkdown";
import { DiffViewer, type CodeCommentMode, type DiffLineBookmark, type DiffLineComment, type DiffLineReveal } from "./DiffViewer";
import { FileTypeIcon } from "./FileTypeIcon";
import { GuideProse } from "./GuideProse";
import { FileChatPopup } from "./FileChatPopup";
import { FileTree, type FileReviewProgress, type FileStepGroup } from "./FileTree";
import { LayerPresentation } from "./LayerPresentation";
import { FileDiffSkeleton } from "./LoadingSkeleton";
import { repositoryShortName } from "../lib/pull-view";

const ChewMarkdown = lazy(() => import("./ChewMarkdown").then((module) => ({ default: module.ChewMarkdown })));

interface LayerViewProps {
  layer: GuideLayer;
  reviewMode: ReviewPresentation;
  /** True while the reviewer is walking the author's order rather than the whole diff. */
  following: boolean;
  onFollowingChange: (following: boolean) => void;
  guide: ReviewGuide;
  compilation: CodeMapCompilation;
  status: LayerStatus;
  stepNumber: number;
  totalSteps: number;
  initialStopId?: string;
  initialViewState?: ReviewLayerViewState;
  savedCollapsedPaths?: readonly string[];
  savedCollapsedPathsUpdatedAt?: string;
  positionRevision: number;
  prompts: GuidePrompt[];
  files: FixtureFiles;
  descriptor: FixtureDescriptor;
  pullDetails?: HostedPreview["pull"];
  pullContext?: HostedReviewContext;
  drafts: DraftComment[];
  regionStatuses: Record<string, RegionStatus>;
  focusStopStatuses: Record<string, "visited">;
  allTourStops: TourStop[];
  changedSincePaths?: Set<string>;
  onDraft: (itemId: string, body: string, anchor?: DraftAnchor, kind?: DraftCommentKind) => void | Promise<void>;
  onDeleteDraft: (itemId: string) => void | Promise<void>;
  onStatus: (status: LayerStatus) => void;
  onRegionPresented: (regionId: string) => void;
  onFocusStopVisited: (stopId: string) => void;
  onFocusChange: (stopId: string, source: "navigation" | "scroll") => void;
  onFeedback: (itemId: string | undefined, message: string) => void;
  onOpenOverview: () => void;
  onOpenReceipt: () => void;
  onSelectStep: (index: number) => void;
  onPrevious: () => void;
  onNext: () => void;
  onNeedFile?: (path: string) => void;
  onNeedFiles?: (paths: string[]) => void;
  onSelectCode?: (target: ChewCodeTarget) => void;
  onViewStateChange: (state: Omit<ReviewLayerViewState, "updatedAt">) => void | Promise<void>;
  onReplyThread?: (commentId: number, body: string) => Promise<HostedConversationComment>;
  toolbarAction?: ReactNode;
  unguided?: boolean;
  defaultFileOrder: FileOrderMode;
  collapseByDefault: readonly string[];
  reviewSteps: Array<{ id: string; index: number; scrutiny: GuideLayer["scrutiny"]; status: LayerStatus; title: string }>;
}

interface DraftSave {
  anchor?: DraftAnchor;
  body: string;
  itemId: string;
  kind?: DraftCommentKind;
}

type SidebarMode = "guide" | "pr";

const REVIEW_SCROLL_STEP_PX = 6 * 19;
const FILE_ORDER_STORAGE_KEY = "chew:file-order:v1";
const CODE_WRAP_STORAGE_KEY = "chew:code-wrap:v1";
const SPLIT_VIEW_STORAGE_KEY = "chew:split-view:v1";
const CODE_COMMENT_STORAGE_KEY = "chew:code-comments:v1";

const fileOrderOptions: Array<{ mode: FileOrderMode; label: string; description: string }> = [
  { mode: "structural-story", label: "Structural story", description: "Contracts and entrypoints, then core behaviour, proof, and generated output." },
  { mode: "review-priority", label: "Review priority", description: "Deep and consequential code before routine structure or evidence." },
  { mode: "author-order", label: "Author order", description: "The explicit file sequence authored into this guided step." },
  { mode: "alphabetical", label: "Alphabetical", description: "A predictable path-based order similar to GitHub." },
];

function storedFileOrder(fallback: FileOrderMode): FileOrderMode {
  const stored = localStorage.getItem(FILE_ORDER_STORAGE_KEY);
  return fileOrderModes.includes(stored as FileOrderMode) ? stored as FileOrderMode : fallback;
}

function FileOrderPicker({ mode, onChange }: { mode: FileOrderMode; onChange: (mode: FileOrderMode) => void }) {
  const guideEnabled = useGuideEnabled();
  const options = fileOrderOptions.map((option) => !guideEnabled && option.mode === "author-order"
    ? { ...option, label: "File sequence", description: "The original changed-file sequence." }
    : option);
  const selected = options.find((option) => option.mode === mode)!;
  return (
    <Popover>
      <PopoverTrigger render={<Button aria-label={`File order: ${selected.label}`} className="file-order-trigger" size="sm" title="Change file order" type="button" variant="ghost" />}>
        <ListOrdered aria-hidden="true" /><span>{selected.label}</span><ChevronDown aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent align="end" className="file-order-popover" side="bottom" sideOffset={6}>
        <PopoverHeader><PopoverTitle>File order</PopoverTitle><PopoverDescription>Choose the file order for keyboard traversal.</PopoverDescription></PopoverHeader>
        <div aria-label="File ordering lens" role="menu">
          {options.map((option) => (
            <button aria-checked={mode === option.mode} key={option.mode} onClick={() => onChange(option.mode)} role="menuitemradio" type="button">
              <span><strong>{option.label}</strong><small>{option.description}</small></span>
              {mode === option.mode && <Check aria-hidden="true" />}
            </button>
          ))}
        </div>
        <p>Saved as your default for future reviews.</p>
      </PopoverContent>
    </Popover>
  );
}

function CodeCommentPicker({ mode, onChange }: { mode: CodeCommentMode; onChange: (mode: CodeCommentMode) => void }) {
  return (
    <Popover>
      <PopoverTrigger render={<Button aria-label={`Code comments: ${mode === "show" ? "shown" : "extracted"}`} className="code-comment-trigger" size="sm" title="Code comment visibility" type="button" variant="ghost" />}>
        <MessageSquareText aria-hidden="true" /><span>Code comments</span><ChevronDown aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent align="end" className="code-comment-popover" side="bottom" sideOffset={6}>
        <PopoverHeader><PopoverTitle>Code comments</PopoverTitle><PopoverDescription>Reduce source commentary without hiding that it exists.</PopoverDescription></PopoverHeader>
        <div aria-label="Code comment visibility" role="radiogroup">
          <button aria-checked={mode === "show"} onClick={() => onChange("show")} role="radio" type="button"><span><strong>Show inline</strong><small>Render comments as normal code.</small></span>{mode === "show" && <Check aria-hidden="true" />}</button>
          <button aria-checked={mode === "extract"} onClick={() => onChange("extract")} role="radio" type="button"><span><strong>Extract</strong><small>Replace comment text with a hoverable marker.</small></span>{mode === "extract" && <Check aria-hidden="true" />}</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function StepNavigator({
  currentStepId,
  effort,
  estimatedMinutes,
  fileCount,
  focusCount,
  onSelect,
  steps,
}: {
  currentStepId: string;
  effort: "quick" | "focused" | "heavy";
  estimatedMinutes: number;
  fileCount: number;
  focusCount: number;
  onSelect: (index: number) => void;
  steps: LayerViewProps["reviewSteps"];
}) {
  const current = steps.find((step) => step.id === currentStepId) ?? steps[0];
  if (!current) return null;
  return (
    <div className="guide-step-context">
      <Popover>
        <PopoverTrigger render={<button className="guide-step-picker" type="button" />}>
          <span>{String(current.index + 1).padStart(2, "0")} / {String(steps.length).padStart(2, "0")}</span>
          <strong>{current.title}</strong>
          <ChevronDown aria-hidden="true" />
        </PopoverTrigger>
        <PopoverContent align="start" className="guide-step-popover" side="right" sideOffset={8}>
          <PopoverHeader><PopoverTitle>Review steps</PopoverTitle><PopoverDescription>Jump without losing progress in the current step.</PopoverDescription></PopoverHeader>
          <ol>{steps.map((step) => {
            const complete = ["done", "question", "problem"].includes(step.status);
            const active = step.id === currentStepId;
            return (
              <li className={active ? "current" : complete ? "complete" : ""} key={step.id}>
                <button onClick={() => onSelect(step.index)} type="button">
                  <span>{complete ? "✓" : step.index + 1}</span>
                  <div><strong>{step.title}</strong><small>{step.scrutiny} · {active ? "current" : step.status.replace("-", " ")}</small></div>
                </button>
              </li>
            );
          })}</ol>
        </PopoverContent>
      </Popover>
      <div className="guide-step-posture">
        <ScrutinyBadge scrutiny={current.scrutiny} />
        <span className={`step-effort effort-${effort}`}>{effort} effort</span>
        <span>~{estimatedMinutes} min</span>
        <span>{fileCount} {fileCount === 1 ? "file" : "files"}</span>
        <span>{focusCount} {focusCount === 1 ? "focus" : "focuses"}</span>
      </div>
    </div>
  );
}

function ScrutinyBadge({ scrutiny }: { scrutiny: GuideLayer["scrutiny"] }) {
  const labels = { scan: "Scan", standard: "Standard", deep: "Deep" };
  return <span className={`scrutiny-badge scrutiny-badge-${scrutiny}`}>{labels[scrutiny]}</span>;
}

function ToolButton({
  children,
  label,
  onClick,
  pressed,
  shortcut,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
  pressed?: boolean;
  shortcut?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger render={<Button aria-label={label} aria-pressed={pressed} className={pressed ? "tool-button-active" : undefined} onClick={onClick} size="icon-xs" type="button" variant="ghost" />}>
        {children}
      </TooltipTrigger>
      <TooltipContent side="bottom" sideOffset={5}>{label}{shortcut ? <> <kbd>{shortcut}</kbd></> : null}</TooltipContent>
    </Tooltip>
  );
}

function storedPanelWidth(key: string, minimum: number, maximum: number) {
  const stored = Number(localStorage.getItem(key));
  return Number.isFinite(stored) && stored > 0 ? Math.max(minimum, Math.min(maximum, stored)) : undefined;
}

function responsivePanelWidth(panel: "guide" | "tree") {
  return panel === "guide" ? 370 : 300;
}

function pathParts(path: string) {
  const parts = path.split("/");
  return { fileName: parts.at(-1) ?? path, directory: parts.slice(0, -1).join("/") };
}

const commentKindLabels: Record<DraftCommentKind, string> = {
  note: "Note",
  question: "Question",
  tip: "Tip",
  change: "Change",
};

function anchorLabel(anchor: DraftAnchor) {
  const { startLine, endLine } = draftAnchorSpan(anchor);
  return lineRangeLabel(anchor.side, startLine, endLine);
}

function anchorSpansRange(anchor: DraftAnchor) {
  const { startLine, endLine } = draftAnchorSpan(anchor);
  return startLine !== endLine;
}

function lineRangeLabel(side: "LEFT" | "RIGHT", startLine: number, endLine: number) {
  const prefix = side === "LEFT" ? "removed line" : "line";
  return startLine === endLine ? `${prefix} ${startLine}` : `${prefix}s ${startLine}–${endLine}`;
}

function pullDate(value?: string) {
  if (!value) return undefined;
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" }).format(new Date(value));
}

function RenderedMarkdown({ body, codePaths, onSelectCode, repository, showTableOfContents }: { body: string; codePaths?: string[]; onSelectCode?: (target: ChewCodeTarget) => void; repository?: string; showTableOfContents?: boolean }) {
  return <Suspense fallback={<div className="markdown-loading">Formatting description…</div>}><ChewMarkdown body={body} codePaths={codePaths} onSelectCode={onSelectCode} repository={repository} showTableOfContents={showTableOfContents} /></Suspense>;
}

const PullInfoPanel = memo(function PullInfoPanel({
  descriptor,
  guide,
  onOpenOverview,
  onPreviewCode,
  pullContext,
  pullDetails,
}: {
  descriptor: FixtureDescriptor;
  guide: ReviewGuide;
  onOpenOverview: () => void;
  onPreviewCode?: (target: ChewCodeTarget) => void;
  pullContext?: HostedReviewContext;
  pullDetails?: HostedPreview["pull"];
}) {
  const ci = pullContext?.ci;
  const reviewers = [...(pullDetails?.requestedReviewers ?? []), ...(pullDetails?.requestedTeams ?? [])];
  const description = pullDetails?.body?.trim() || descriptor.body?.trim();
  const changed = pullContext?.changedSinceReview;
  const authorSweep = pullContext?.authorSweep;
  const additions = pullDetails?.additions ?? descriptor.stats.additions;
  const deletions = pullDetails?.deletions ?? descriptor.stats.deletions;
  const changedFiles = pullDetails?.changedFiles ?? descriptor.stats.changedFiles;
  const commits = pullDetails?.commits ?? descriptor.stats.commits;
  const pullUrl = descriptor.url ?? `https://github.com/${descriptor.repository}/pull/${descriptor.pullNumber}`;
  const codePaths = useMemo(() => guide.coverage.files.map((file) => file.path), [guide.coverage.files]);
  const ciLabel = checksSignal(ci).full;

  return (
    <div className="pr-info-panel">
      <header>
        <span>{repositoryShortName(descriptor.repository)} #{descriptor.pullNumber}</span>
        <h1>{descriptor.title}</h1>
        <div className="pr-info-actions">
          <button onClick={onOpenOverview} type="button"><Info aria-hidden="true" />CHEW overview</button>
          <a href={pullUrl} rel="noreferrer" target="_blank">Open on GitHub <ExternalLink aria-hidden="true" /></a>
        </div>
      </header>

      <div className={`pr-ci-state ci-${ci?.state ?? "unavailable"}`}><span aria-hidden="true" /><strong>{ciLabel}</strong>{ci && ci.total > 0 && <small>{ci.succeeded}/{ci.total}</small>}</div>

      {authorSweep && authorSweep.status !== "none" && (
        <div className={`pr-author-sweep sweep-${authorSweep.status}`}>
          <Check aria-hidden="true" />
          <div>
            <strong>{authorSweep.status === "current" ? "Author sweep complete" : "Previous author sweep is stale"}</strong>
            <small>@{authorSweep.author} · {authorSweep.reviewedHeadSha?.slice(0, 8)}{authorSweep.completedAt ? ` · ${pullDate(authorSweep.completedAt)}` : ""}</small>
          </div>
        </div>
      )}

      <dl className="pr-facts">
        <div><dt><UserRound aria-hidden="true" />Author</dt><dd>{pullDetails ? `@${pullDetails.author}` : "\u2014"}</dd></div>
        <div><dt><UserRound aria-hidden="true" />Reviewers</dt><dd>{reviewers.length > 0 ? reviewers.map((reviewer) => `@${reviewer}`).join(", ") : "None requested"}</dd></div>
        <div><dt><GitBranch aria-hidden="true" />Branch</dt><dd>{pullDetails ? `${pullDetails.baseRef} ← ${pullDetails.headRef}` : guide.scope.stack.role === "standalone" ? "Standalone change" : `Stack ${guide.scope.stack.position}/${guide.scope.stack.total}`}</dd></div>
      </dl>

      <section className="pr-change-facts">
        <div><strong>{changedFiles}</strong><span>files</span></div>
        <div><strong className="addition">+{additions}</strong><span>added</span></div>
        <div><strong className="deletion">−{deletions}</strong><span>removed</span></div>
        <div><strong>{commits}</strong><span>commits</span></div>
      </section>

      {pullDetails && <div className="pr-secondary-facts"><span>Opened {pullDate(pullDetails.createdAt) ?? "recently"}</span>{pullDetails.labels.length > 0 && <span>{pullDetails.labels.join(" · ")}</span>}</div>}

      {description ? (
        <section className="pr-description"><h2>PR description</h2><RenderedMarkdown body={description} codePaths={codePaths} onSelectCode={onPreviewCode} repository={descriptor.repository} showTableOfContents /></section>
      ) : (
        <section className="pr-description pr-description-fallback">
          <h2>Change context</h2>
          <RenderedMarkdown body={[
            guide.overview.problem,
            guide.overview.approach,
            guide.overview.summary.length > 0 ? guide.overview.summary.map((item) => `- ${item}`).join("\n") : "",
          ].filter(Boolean).join("\n\n")} codePaths={codePaths} onSelectCode={onPreviewCode} repository={descriptor.repository} showTableOfContents />
        </section>
      )}

      {changed && <section className="pr-since-review"><h2>Since your review</h2>{changed.status === "changed" ? <><p><strong>{changed.basis === "current-pr-files" ? `${changed.files.length} current PR files differ` : `${changed.commits} new commits`}</strong> · +{changed.additions} −{changed.deletions}</p>{changed.message && <small>{changed.message}</small>}</> : <p>{changed.status === "current" ? changed.message ?? "No changes in the current PR." : changed.status === "never-reviewed" ? "You have not reviewed this head yet." : changed.message ?? "Review history unavailable."}</p>}</section>}
    </div>
  );
});

function CodeReferencePreview({ file, onClose, onOpenInReview, target }: { file?: FixtureFile; onClose: () => void; onOpenInReview: (target: ChewCodeTarget) => void; target?: ChewCodeTarget }) {
  if (!target) return null;
  const loading = !file || file.loading;
  const source = file ? file.head || file.base : "";
  const lines = source.split("\n");
  const anchor = target.startLine ?? 1;
  const firstLine = Math.max(1, anchor - 7);
  const lastLine = Math.min(lines.length, Math.max(target.endLine ?? anchor, anchor + 7));
  const visible = lines.slice(firstLine - 1, lastLine);
  const fileName = target.path.split("/").at(-1) ?? target.path;
  return (
    <Dialog onOpenChange={(open) => { if (!open) onClose(); }} open>
      <DialogContent className="code-reference-dialog" showCloseButton>
        <header className="code-reference-dialog-header">
          <div><FileTypeIcon path={target.path} /><span>{target.path.slice(0, Math.max(0, target.path.length - fileName.length))}</span><strong>{fileName}</strong></div>
          <DialogTitle>{target.startLine ? `Lines ${target.startLine}${target.endLine && target.endLine !== target.startLine ? `–${target.endLine}` : ""}` : "Changed file"}</DialogTitle>
          <DialogDescription>Quick context from the current PR head.</DialogDescription>
        </header>
        <div className="code-reference-peek">
          {loading ? <FileDiffSkeleton path={target.path} /> : file?.loadError ? <p>{file.loadError}</p> : visible.length > 0 ? visible.map((line, index) => {
            const lineNumber = firstLine + index;
            const highlighted = target.startLine !== undefined && lineNumber >= target.startLine && lineNumber <= (target.endLine ?? target.startLine);
            return <div className={highlighted ? "highlighted" : ""} key={lineNumber}><span>{lineNumber}</span><code>{line || " "}</code></div>;
          }) : <p>No current-head source is available for this file.</p>}
        </div>
        <footer>
          <Button onClick={onClose} type="button" variant="ghost">Close</Button>
          <Button onClick={() => { onClose(); onOpenInReview(target); }} type="button">Open in code review</Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

function HiddenFilePicker({
  hiddenPaths,
  files,
  onSelect,
}: {
  hiddenPaths: string[];
  files: FixtureFiles;
  onSelect: (path: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const choices = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return hiddenPaths
      .filter((path) => !normalized || path.toLowerCase().includes(normalized))
      .sort((left, right) => left.localeCompare(right));
  }, [hiddenPaths, query]);

  return (
    <Popover
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setQuery("");
      }}
      open={open}
    >
      <PopoverTrigger
        render={<button aria-label={`Hidden files: ${hiddenPaths.length}`} className="file-context-collapsed" title="Show files outside this guided step" type="button" />}
      >
        <span>•••</span>
        <strong>{hiddenPaths.length > 0 ? `${hiddenPaths.length} hidden ${hiddenPaths.length === 1 ? "file" : "files"}` : "No hidden files"}</strong>
        <small>Outside this step</small>
      </PopoverTrigger>
      <PopoverContent align="start" className="other-files-popover" side="bottom" sideOffset={6}>
        <PopoverHeader>
          <PopoverTitle>Files outside this step</PopoverTitle>
          <PopoverDescription>Open any hidden file without losing your guided-review progress.</PopoverDescription>
        </PopoverHeader>
        <input
          aria-label="Find hidden file"
          autoFocus
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter hidden files…"
          value={query}
        />
        <div className="other-files-list">
          {choices.map((path) => {
            const { directory, fileName } = pathParts(path);
            return (
              <button
                key={path}
                onClick={() => {
                  onSelect(path);
                  setOpen(false);
                }}
                type="button"
              >
                <FileTypeIcon compact language={files.files[path]?.language} path={path} />
                <span><strong>{fileName}</strong><small>{directory}</small></span>
              </button>
            );
          })}
          {choices.length === 0 && <p>{hiddenPaths.length === 0 ? "The guide includes every changed file." : `No hidden file matches “${query}”.`}</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface ExactSearchResult {
  id: string;
  path: string;
  side: "LEFT" | "RIGHT";
  line: number;
  text: string;
  change: "add" | "delete" | "context";
}

function ExactMatch({ query, text }: { query: string; text: string }) {
  const index = text.toLocaleLowerCase().indexOf(query.toLocaleLowerCase());
  if (index < 0) return <>{text}</>;
  return <>{text.slice(0, index)}<mark>{text.slice(index, index + query.length)}</mark>{text.slice(index + query.length)}</>;
}

function CodeSearchPopover({
  loadingPaths,
  onLoadAll,
  onOpenChange,
  onQueryChange,
  onSelect,
  open,
  query,
  results,
}: {
  loadingPaths: string[];
  onLoadAll: () => void;
  onOpenChange: (open: boolean) => void;
  onQueryChange: (query: string) => void;
  onSelect: (result: ExactSearchResult) => void;
  open: boolean;
  query: string;
  results: ExactSearchResult[];
}) {
  return (
    <Popover onOpenChange={onOpenChange} open={open}>
      <PopoverTrigger
        render={<Button aria-label="Search changed code" className="code-search-trigger" size="sm" title="Search changed code · ⌘F" type="button" variant="ghost" />}
      ><Search aria-hidden="true" /><span>Search</span><kbd>⌘F</kbd></PopoverTrigger>
      <PopoverContent align="end" className="code-search-popover" side="bottom" sideOffset={6}>
        <PopoverHeader><PopoverTitle>Exact code search</PopoverTitle><PopoverDescription>Literal matches in loaded changed files.</PopoverDescription></PopoverHeader>
        <input
          aria-label="Exact code search"
          autoFocus
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && results[0]) {
              event.preventDefault();
              onSelect(results[0]);
            }
          }}
          placeholder="Search code exactly…"
          spellCheck={false}
          value={query}
        />
        {loadingPaths.length > 0 && (
          <button className="search-load-all" onClick={onLoadAll} type="button">
            Search all {loadingPaths.length} unloaded {loadingPaths.length === 1 ? "file" : "files"}
          </button>
        )}
        <div className="code-search-results">
          {query && results.map((result) => {
            const { directory, fileName } = pathParts(result.path);
            return (
              <button key={result.id} onClick={() => onSelect(result)} type="button">
                <span className={`search-change search-change-${result.change}`}>{result.change === "add" ? "+" : result.change === "delete" ? "−" : "·"}</span>
                <span className="search-result-copy"><span><strong>{fileName}</strong><small>{directory}</small><em>{lineRangeLabel(result.side, result.line, result.line)}</em></span><code><ExactMatch query={query} text={result.text} /></code></span>
              </button>
            );
          })}
          {!query && <p>Type a literal string. Regular expressions and fuzzy matching are intentionally off.</p>}
          {query && results.length === 0 && <p>No exact matches in loaded changed files.</p>}
          {results.length >= 200 && <small>Showing the first 200 matches.</small>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function BookmarksPopover({
  bookmarks,
  files,
  headSha,
  onMarkReviewed,
  onOpenChange,
  onRemove,
  onSelect,
  open,
}: {
  bookmarks: ReviewBookmark[];
  files: FixtureFiles;
  headSha: string;
  onMarkReviewed: (bookmark: ReviewBookmark) => void;
  onOpenChange: (open: boolean) => void;
  onRemove: (bookmark: ReviewBookmark) => void;
  onSelect: (bookmark: ReviewBookmark) => void;
  open: boolean;
}) {
  return (
    <Popover onOpenChange={onOpenChange} open={open}>
      <PopoverTrigger
        render={<Button aria-label={`Bookmarks (${bookmarks.length})`} className="bookmarks-trigger" size="sm" title="Saved code locations" type="button" variant="ghost" />}
      ><Bookmark aria-hidden="true" /><span>Bookmarks</span>{bookmarks.length > 0 && <em>{bookmarks.length}</em>}</PopoverTrigger>
      <PopoverContent align="end" className="bookmarks-popover" side="bottom" sideOffset={6}>
        <PopoverHeader><PopoverTitle>Saved locations</PopoverTitle><PopoverDescription>Head changes never silently move these anchors.</PopoverDescription></PopoverHeader>
        <div className="bookmark-list">
          {bookmarks.map((bookmark) => {
            const freshness = bookmarkFreshness(bookmark, headSha, files.files[bookmark.path]);
            const { directory, fileName } = pathParts(bookmark.path);
            return (
              <article className={`bookmark-item bookmark-${freshness}`} key={bookmark.id}>
                <button className="bookmark-location" onClick={() => onSelect(bookmark)} type="button">
                  <span><strong>{fileName}</strong><small>{directory}</small><em>{lineRangeLabel(bookmark.side, bookmark.line, bookmark.line)}</em></span>
                  <code>{bookmark.lineText.trim() || "Blank line"}</code>
                  <i>{freshness === "current" ? "Current" : freshness === "needs-review" ? "Re-review on this head" : freshness === "loading" ? "File not loaded" : "Original line changed or missing"}</i>
                </button>
                <div>
                  {freshness === "needs-review" && <button aria-label={`Mark ${fileName} ${lineRangeLabel(bookmark.side, bookmark.line, bookmark.line)} reviewed`} onClick={() => onMarkReviewed(bookmark)} title="Mark reviewed on this head" type="button"><Check aria-hidden="true" /></button>}
                  <button aria-label={`Remove bookmark from ${fileName} ${lineRangeLabel(bookmark.side, bookmark.line, bookmark.line)}`} onClick={() => onRemove(bookmark)} title="Remove bookmark" type="button"><X aria-hidden="true" /></button>
                </div>
              </article>
            );
          })}
          {bookmarks.length === 0 && <p>Use the bookmark icon beside a code line to save it here.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const EMPTY_LINE_COMMENTS: DiffLineComment[] = [];
const EMPTY_LINE_BOOKMARKS: DiffLineBookmark[] = [];
const EMPTY_ANNOTATIONS: ReviewAnnotation[] = [];
const EMPTY_REGIONS: CompiledRegion[] = [];

const StepFileDiff = memo(function StepFileDiff({
  activeAnchor,
  annotations,
  bookmarks,
  codeCommentMode,
  collapsed,
  comments,
  descriptor,
  file,
  files,
  focus,
  onSelectComment,
  onToggleBookmark,
  onToggleFile,
  onToggleViewed,
  path,
  pullContext,
  pullDetails,
  reviewGuidance,
  regions,
  revealLine,
  onLineRevealed,
  revealToken,
  reviewThreads,
  codePaths,
  onReplyThread,
  onSelectLocation,
  onPreviewCode,
  selected,
  viewedState,
  wrapCode,
  splitView,
}: {
  annotations: ReviewAnnotation[];
  activeAnchor?: DraftAnchor;
  bookmarks: DiffLineBookmark[];
  codeCommentMode: CodeCommentMode;
  collapsed: boolean;
  comments: DiffLineComment[];
  descriptor: FixtureDescriptor;
  file?: FixtureFile;
  files: FixtureFiles;
  focus?: TourStop;
  onSelectComment: (anchor: DraftAnchor) => void;
  onToggleBookmark: (anchor: DraftAnchor, lineText: string) => void;
  onToggleFile: (path: string) => void;
  onToggleViewed: (path: string) => void;
  path: string;
  pullContext?: HostedReviewContext;
  pullDetails?: HostedPreview["pull"];
  reviewGuidance: NonNullable<FileChatContextInput["reviewGuidance"]>;
  regions: CompiledRegion[];
  revealLine?: DiffLineReveal;
  onLineRevealed: (token: number) => void;
  revealToken: number;
  reviewThreads: HostedReviewThread[];
  codePaths: string[];
  onReplyThread?: (commentId: number, body: string) => Promise<HostedConversationComment>;
  onSelectLocation: (path: string, line?: number, side?: "LEFT" | "RIGHT") => void;
  onPreviewCode: (target: ChewCodeTarget) => void;
  selected: boolean;
  viewedState?: ViewedFileState;
  wrapCode: boolean;
  splitView: boolean;
}) {
  const { directory, fileName } = pathParts(path);
  const rows = useMemo(
    () => file && !file.binary ? buildUnifiedDiff(file.base, file.head) : [],
    [file],
  );
  const stats = useMemo(() => diffStats(rows), [rows]);

  return (
    <section
      aria-label={`Changed file ${path}`}
      className={`step-file${selected ? " selected" : ""}${focus ? " focused" : ""}${collapsed ? " collapsed" : ""}`}
      data-file-path={path}
    >
      <header className="editor-toolbar step-file-header">
        <button
          aria-expanded={!collapsed}
          className="editor-file-toggle"
          onClick={() => onToggleFile(path)}
          title={`${collapsed ? "Expand" : "Collapse"} ${path}`}
          type="button"
        >
          <ChevronDown aria-hidden="true" />
          <FileTypeIcon language={file?.language} path={path} />
          <span className="editor-file-identity"><span>{directory ? `${directory}/` : ""}</span><strong>{fileName}</strong></span>
          {file && !file.binary && (
            <span className="editor-file-stats"><b>+{stats.additions}</b><i>−{stats.deletions}</i></span>
          )}
          {reviewThreads.some((thread) => !thread.resolved && !thread.outdated) && (
            <span className="editor-file-threads"><MessageSquareText aria-hidden="true" />{reviewThreads.filter((thread) => !thread.resolved && !thread.outdated).length}</span>
          )}
        </button>
        {focus && (
            <span className={`editor-focus-label focus-${focus.scrutiny}`}>
              {focus.scrutiny} · {lineRangeLabel(focus.side, focus.startLine, focus.endLine)}
          </span>
        )}
        <div className="step-file-actions">
          <button
            aria-pressed={viewedState === "viewed"}
            className={`file-viewed-toggle${viewedState === "viewed" ? " is-viewed" : viewedState === "changed" ? " is-changed" : ""}`}
            onClick={() => onToggleViewed(path)}
            title={viewedState === "viewed"
              ? `Marked viewed — click to mark ${fileName} as not viewed`
              : viewedState === "changed"
                ? `${fileName} changed since you viewed it — click to mark the new diff viewed`
                : `Mark ${fileName} as viewed`}
            type="button"
          >
            <Check aria-hidden="true" />
            {viewedState === "changed" ? "Changed" : "Viewed"}
          </button>
          <FileChatPopup
            onSelectCode={onPreviewCode}
            descriptor={descriptor}
            file={file}
            files={files}
            path={path}
            pullContext={pullContext}
            pullDetails={pullDetails}
            reviewThreads={reviewThreads}
            reviewGuidance={reviewGuidance}
            fileSemantics={{
              focus: focus ? {
                side: focus.side,
                startLine: focus.startLine,
                endLine: focus.endLine,
                nature: focus.nature,
                concerns: focus.concerns,
                scrutiny: focus.scrutiny,
                coverageMode: focus.coverageMode,
              } : undefined,
              regions: regions.map((region) => ({
                nature: region.nature,
                concerns: region.concerns,
                scrutiny: region.scrutiny,
                verification: region.verification,
                changedLoc: region.changedLoc,
              })),
              annotations: annotations.map((annotation) => ({
                side: annotation.side,
                startLine: annotation.startLine,
                endLine: annotation.endLine,
                tone: annotation.tone,
                body: annotation.body,
              })),
            }}
          />
        </div>
      </header>
      {!collapsed && (
        <div className="step-file-body">
          {file?.loading ? (
            <FileDiffSkeleton path={path} />
          ) : file?.loadError ? (
            <div className="missing-code"><strong>File temporarily unavailable</strong><p>{file.loadError}</p></div>
          ) : !file ? (
            <div className="missing-code"><strong>Source unavailable</strong><p>This file was not bundled into the local fixture.</p></div>
          ) : file.binary ? (
            <BinaryFilePreview file={file} path={path} />
          ) : (
            <DiffViewer
              activeAnchor={activeAnchor}
              annotations={annotations}
              bookmarks={bookmarks}
              codeCommentMode={codeCommentMode}
              comments={comments}
              file={file}
              focus={focus}
              onSelectComment={onSelectComment}
              onToggleBookmark={onToggleBookmark}
              path={path}
              regions={regions}
              revealLine={revealLine}
              onLineRevealed={onLineRevealed}
              revealToken={revealToken}
              reviewThreads={reviewThreads}
              codePaths={codePaths}
              onReplyThread={onReplyThread}
              onSelectLocation={onSelectLocation}
              rows={rows}
              wrapCode={wrapCode}
              splitView={splitView}
            />
          )}
        </div>
      )}
    </section>
  );
});

export function LayerView({
  layer,
  reviewMode,
  following,
  onFollowingChange,
  unguided = false,
  guide,
  compilation,
  status,
  stepNumber,
  totalSteps,
  initialStopId,
  initialViewState,
  savedCollapsedPaths,
  savedCollapsedPathsUpdatedAt,
  positionRevision,
  prompts,
  files,
  descriptor,
  pullDetails,
  pullContext,
  drafts,
  regionStatuses,
  focusStopStatuses,
  allTourStops,
  changedSincePaths = new Set(),
  onDraft,
  onDeleteDraft,
  onStatus,
  onRegionPresented,
  onFocusStopVisited,
  onFocusChange,
  onFeedback,
  onOpenOverview,
  onOpenReceipt,
  onSelectStep,
  onPrevious,
  onNext,
  onNeedFile,
  onNeedFiles,
  onSelectCode,
  onViewStateChange,
  onReplyThread,
  toolbarAction,
  defaultFileOrder,
  collapseByDefault,
  reviewSteps,
}: LayerViewProps) {
  const resolvedItems = useMemo(
    () =>
      layer.items.flatMap((candidate) =>
        candidate.regionIds.map((regionId) => resolveGuideItem(guide, compilation, candidate, regionId)),
      ),
    [compilation, guide, layer.items],
  );
  const authoredTourStops = useMemo(() => resolvedItems.flatMap((candidate) => buildTourStops(candidate)), [resolvedItems]);
  const authoredStepPaths = useMemo(() => [...new Set(resolvedItems.map((candidate) => candidate.path))], [resolvedItems]);
  const [fileOrder, setFileOrder] = useState<FileOrderMode>(() => storedFileOrder(defaultFileOrder));
  const stepPaths = useMemo(
    () => orderStepPaths(authoredStepPaths, compilation.regions, fileOrder),
    [authoredStepPaths, compilation.regions, fileOrder],
  );
  const orderedResolvedItems = useMemo(() => {
    const pathRank = new Map(stepPaths.map((path, index) => [path, index]));
    return resolvedItems
      .map((item, index) => ({ item, index }))
      .sort((left, right) => (pathRank.get(left.item.path) ?? Number.MAX_SAFE_INTEGER) - (pathRank.get(right.item.path) ?? Number.MAX_SAFE_INTEGER) || left.index - right.index)
      .map(({ item }) => item);
  }, [resolvedItems, stepPaths]);
  const tourStops = useMemo(() => {
    const pathRank = new Map(stepPaths.map((path, index) => [path, index]));
    return authoredTourStops
      .map((stop, index) => ({ stop, index }))
      .sort((left, right) => (pathRank.get(left.stop.path) ?? Number.MAX_SAFE_INTEGER) - (pathRank.get(right.stop.path) ?? Number.MAX_SAFE_INTEGER) || left.index - right.index)
      .map(({ stop }) => stop);
  }, [authoredTourStops, stepPaths]);
  const [stopIndex, setStopIndex] = useState(0);
  const [selectedPath, setSelectedPath] = useState(initialViewState?.selectedPath ?? tourStops[0]?.path ?? "");
  const [feedback, setFeedback] = useState("");
  const [treeExpanded, setTreeExpanded] = useState(() => reviewMode === "fast-lane" ? false : !["closed", "collapsed"].includes(localStorage.getItem("chew:panel:right:v3") ?? ""));
  const [guideExpanded, setGuideExpanded] = useState(() =>
    unguided || (following && !["closed", "collapsed"].includes(localStorage.getItem("chew:panel:left:v3") ?? "")));
  // A guided review shows its guide, full stop: the PR context already opened
  // the review on the briefing, so a "PR info" tab here only competed with the
  // authored narrative (and its abbreviated file facts read as missing data).
  // Unguided reviews have no narrative, so the PR panel is their one pane.
  const sidebarMode: SidebarMode = unguided ? "pr" : "guide";
  // Guided traversal is opt-in and owned by the workspace, so the header
  // toggle and the in-pane controls can never disagree about which mode you
  // are in.
  const fullDiffOpen = !following;
  const setFullDiffOpen = (value: boolean) => onFollowingChange(!value);
  const [codePreviewTarget, setCodePreviewTarget] = useState<ChewCodeTarget>();
  const [guideWidth, setGuideWidth] = useState<number | undefined>(() => storedPanelWidth("chew:panel:left-width:v2", 220, 480));
  const [treeWidth, setTreeWidth] = useState<number | undefined>(() => storedPanelWidth("chew:panel:right-width:v2", 220, 480));
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [exitTarget, setExitTarget] = useState<"briefing">();
  const [activeLineAnchor, setActiveLineAnchor] = useState<DraftAnchor>();
  const [revealRequest, setRevealRequest] = useState({ path: "", token: 0 });
  const [lineRevealRequest, setLineRevealRequest] = useState<DiffLineReveal & { path: string }>({ path: "", side: "RIGHT", line: 1, token: 0 });
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  const [wrapCode, setWrapCode] = useState(() => localStorage.getItem(CODE_WRAP_STORAGE_KEY) !== "false");
  const [splitView, setSplitView] = useState(() => localStorage.getItem(SPLIT_VIEW_STORAGE_KEY) === "true");
  const [codeCommentMode, setCodeCommentMode] = useState<CodeCommentMode>(() => localStorage.getItem(CODE_COMMENT_STORAGE_KEY) === "extract" ? "extract" : "show");

  useEffect(() => {
    if (codePreviewTarget && files.files[codePreviewTarget.path]?.loading) onNeedFile?.(codePreviewTarget.path);
  }, [codePreviewTarget, files.files, onNeedFile]);
  const bookmarkKey = useMemo(
    () => bookmarkStorageKey(descriptor.repository, descriptor.pullNumber),
    [descriptor.pullNumber, descriptor.repository],
  );
  const [bookmarks, setBookmarks] = useState<ReviewBookmark[]>(() => loadBookmarks(bookmarkKey));
  const viewedKey = useMemo(
    () => viewedStorageKey(descriptor.repository, descriptor.pullNumber),
    [descriptor.pullNumber, descriptor.repository],
  );
  const [viewedMarks, setViewedMarks] = useState(() => loadViewedFiles(viewedKey));
  const [locallyVisitedStops, setLocallyVisitedStops] = useState<Set<string>>(() => new Set());
  const [locallyPresentedRegions, setLocallyPresentedRegions] = useState<Set<string>>(() => new Set());
  const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(() =>
    initialCollapsedFiles(authoredStepPaths, collapseByDefault, savedCollapsedPaths)
  );
  const editorStageRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const scrollCursorLockedUntilRef = useRef(0);
  const viewStateSaveTimerRef = useRef<number | undefined>(undefined);
  const pendingViewStateSaveRef = useRef<(() => void) | undefined>(undefined);
  const viewStateSaveLayerRef = useRef(layer.id);
  const restoredLayerRef = useRef<string | undefined>(undefined);
  const restoredCollapseLayerRef = useRef<string | undefined>(savedCollapsedPaths !== undefined ? layer.id : undefined);

  // Reset per-step state synchronously with the step change. Doing this in an
  // effect would commit one frame pairing the new step with the previous
  // step's collapse set — and any save landing in that frame would write the
  // old step's state under the new step's key.
  const [renderedLayerId, setRenderedLayerId] = useState(layer.id);
  if (renderedLayerId !== layer.id) {
    setRenderedLayerId(layer.id);
    setLocallyVisitedStops(new Set());
    setLocallyPresentedRegions(new Set());
    setCollapsedPaths(initialCollapsedFiles(authoredStepPaths, collapseByDefault, savedCollapsedPaths));
    restoredCollapseLayerRef.current = savedCollapsedPaths !== undefined ? layer.id : undefined;
    setActiveLineAnchor(undefined);
  }

  const item = tourStops[stopIndex] ?? tourStops[0];
  const focus = !fullDiffOpen && item?.path === selectedPath ? item : undefined;
  const focusedPaths = useMemo(() => new Set(stepPaths), [stepPaths]);
  const filePaths = useMemo(() => Object.keys(files.files).sort(), [files.files]);
  const hiddenPaths = useMemo(
    () => filePaths.filter((path) => !focusedPaths.has(path)),
    [filePaths, focusedPaths],
  );
  const loadingPaths = useMemo(
    () => filePaths.filter((path) => files.files[path]?.loading),
    [filePaths, files.files],
  );
  const searchResults = useMemo<ExactSearchResult[]>(() => {
    const query = searchQuery.trim().toLocaleLowerCase();
    if (!query) return [];
    const matches: ExactSearchResult[] = [];
    for (const path of filePaths) {
      const file = files.files[path];
      if (!file || file.loading || file.binary) continue;
      for (const row of buildUnifiedDiff(file.base, file.head)) {
        if (!row.text.toLocaleLowerCase().includes(query)) continue;
        const side = row.kind === "delete" ? "LEFT" : "RIGHT";
        const line = side === "LEFT" ? row.oldLine : row.newLine;
        if (line === undefined) continue;
        matches.push({
          id: `${path}:${side}:${line}:${matches.length}`,
          path,
          side,
          line,
          text: row.text,
          change: row.kind === "add" ? "add" : row.kind === "delete" ? "delete" : "context",
        });
        if (matches.length >= 200) return matches;
      }
    }
    return matches;
  }, [filePaths, files.files, searchQuery]);
  const displayedPaths = useMemo(
    () => fullDiffOpen ? filePaths : selectedPath && !focusedPaths.has(selectedPath) ? [...stepPaths, selectedPath] : stepPaths,
    [filePaths, focusedPaths, fullDiffOpen, selectedPath, stepPaths],
  );
  const activeDraftId = activeLineAnchor
    ? lineDraftItemId(activeLineAnchor)
    : focus ? `stop:${focus.stopId}` : `file:${selectedPath}`;
  const lineCommentsByPath = useMemo(() => drafts.reduce<Map<string, DiffLineComment[]>>((grouped, draft) => {
    const anchor = lineAnchorForDraft(draft);
    if (!anchor || !draft.body.trim()) return grouped;
    const comments = grouped.get(anchor.path) ?? [];
    const { startLine, endLine } = draftAnchorSpan(anchor);
    comments.push({ itemId: draft.itemId, side: anchor.side, line: endLine, startLine, kind: draftCommentKind(draft), body: draft.body });
    grouped.set(anchor.path, comments);
    return grouped;
  }, new Map()), [drafts]);
  const bookmarksByPath = useMemo(() => bookmarks.reduce<Map<string, DiffLineBookmark[]>>((grouped, bookmark) => {
    const values = grouped.get(bookmark.path) ?? [];
    values.push({ side: bookmark.side, line: bookmark.line });
    grouped.set(bookmark.path, values);
    return grouped;
  }, new Map()), [bookmarks]);
  const annotationsByPath = useMemo(() => guide.annotations.reduce<Map<string, ReviewAnnotation[]>>((grouped, annotation) => {
    const annotations = grouped.get(annotation.path) ?? [];
    annotations.push(annotation);
    grouped.set(annotation.path, annotations);
    return grouped;
  }, new Map()), [guide.annotations]);
  const reviewThreadsByPath = useMemo(() => (pullContext?.conversation.threads ?? []).reduce<Map<string, HostedReviewThread[]>>((grouped, thread) => {
    grouped.set(thread.path, [...(grouped.get(thread.path) ?? []), thread]);
    return grouped;
  }, new Map()), [pullContext?.conversation.threads]);
  const regionsByPath = useMemo(() => compilation.regions.reduce<Map<string, CompiledRegion[]>>((grouped, region) => {
    const regions = grouped.get(region.path) ?? [];
    regions.push(region);
    grouped.set(region.path, regions);
    return grouped;
  }, new Map()), [compilation.regions]);
  const persistedDraft = useMemo(
    () => drafts.find((candidate) => candidate.itemId === activeDraftId),
    [activeDraftId, drafts],
  );
  const conversationDraft = drafts.find((candidate) => candidate.itemId === "conversation:new")?.body ?? "";
  const persistedDraftBody = persistedDraft?.body ?? "";
  const persistedDraftKind = persistedDraft ? draftCommentKind(persistedDraft) : "note";
  const [draftBody, setDraftBody] = useState(persistedDraftBody);
  const [draftKind, setDraftKind] = useState<DraftCommentKind>(persistedDraftKind);
  const [draftDirty, setDraftDirty] = useState(false);
  const draftBodyRef = useRef(draftBody);
  const draftKindRef = useRef(draftKind);
  const draftDirtyRef = useRef(false);
  const draftEditedRef = useRef(false);
  const draftTargetRef = useRef(activeDraftId);
  const draftSerializer = useLazySerializer<DraftSave>((draft) =>
    onDraft(draft.itemId, draft.body, draft.anchor, draft.kind));

  const scheduleViewStateSave = useCallback(() => {
    const save = () => {
      viewStateSaveTimerRef.current = undefined;
      pendingViewStateSaveRef.current = undefined;
      // Our own write echoes back through the saved-state live query; latching
      // here keeps that echo from restoring over newer in-memory changes.
      restoredCollapseLayerRef.current = layer.id;
      void onViewStateChange({
        selectedPath,
        collapsedPaths: [...collapsedPaths],
        scrollTop: editorStageRef.current?.scrollTop ?? 0,
      });
    };
    window.clearTimeout(viewStateSaveTimerRef.current);
    // The pending save keeps its own closure so a flush at a step boundary or
    // unmount writes exactly what was scheduled, under the right step key.
    pendingViewStateSaveRef.current = save;
    viewStateSaveTimerRef.current = window.setTimeout(save, 120);
  }, [collapsedPaths, layer.id, onViewStateChange, selectedPath]);

  const flushViewStateSave = useCallback(() => {
    if (viewStateSaveTimerRef.current === undefined) return;
    window.clearTimeout(viewStateSaveTimerRef.current);
    pendingViewStateSaveRef.current?.();
  }, []);

  useEffect(() => {
    const restoringLayer = restoredLayerRef.current !== layer.id;
    const requestedIndex = initialStopId
      ? tourStops.findIndex((stop) => stop.stopId === initialStopId)
      : -1;
    const nextIndex = requestedIndex >= 0 ? requestedIndex : 0;
    const storedPath = restoringLayer && initialViewState?.selectedPath && files.files[initialViewState.selectedPath]
      ? initialViewState.selectedPath
      : undefined;
    const nextPath = storedPath ?? tourStops[nextIndex]?.path ?? Object.keys(files.files)[0] ?? "";
    // Replaying a URL/history position triggers a programmatic reveal. Do not
    // let the resulting scroll event immediately select a nearby overlapping
    // focus and rewrite that history entry.
    scrollCursorLockedUntilRef.current = performance.now() + 400;
    setStopIndex(nextIndex);
    setSelectedPath(nextPath);
    setRevealRequest((current) => ({ path: nextPath, token: current.token + 1 }));
    if (restoringLayer) {
      restoredLayerRef.current = layer.id;
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
        editorStageRef.current?.scrollTo({ top: initialViewState?.scrollTop ?? 0 });
      }));
    }
  }, [initialStopId, positionRevision, tourStops]);

  useEffect(() => {
    if (savedCollapsedPaths === undefined || restoredCollapseLayerRef.current === layer.id) return;
    setCollapsedPaths(new Set(savedCollapsedPaths));
    restoredCollapseLayerRef.current = layer.id;
  }, [savedCollapsedPathsUpdatedAt, layer.id]);

  useEffect(() => {
    // A save still pending for the previous step must land before anything is
    // scheduled against the new one.
    if (viewStateSaveLayerRef.current !== layer.id) {
      flushViewStateSave();
      viewStateSaveLayerRef.current = layer.id;
    }
    scheduleViewStateSave();
  }, [flushViewStateSave, layer.id, scheduleViewStateSave]);

  // Flush rather than drop the pending save: finishing the review, leaving the
  // page, or unmounting must not lose the last collapse or selection. Flushing
  // only at these boundaries keeps the debounce intact within a step.
  useEffect(() => () => flushViewStateSave(), [flushViewStateSave]);
  useEffect(() => {
    window.addEventListener("pagehide", flushViewStateSave);
    return () => window.removeEventListener("pagehide", flushViewStateSave);
  }, [flushViewStateSave]);

  useEffect(() => {
    if (draftTargetRef.current !== activeDraftId) {
      void draftSerializer.flush().catch(() => undefined);
      draftTargetRef.current = activeDraftId;
      draftBodyRef.current = persistedDraftBody;
      draftKindRef.current = persistedDraftKind;
      draftDirtyRef.current = false;
      draftEditedRef.current = false;
      setDraftBody(persistedDraftBody);
      setDraftKind(persistedDraftKind);
      setDraftDirty(false);
      return;
    }
    if (draftEditedRef.current) {
      if (draftDirtyRef.current && persistedDraftBody === draftBodyRef.current && persistedDraftKind === draftKindRef.current) {
        draftDirtyRef.current = false;
        setDraftDirty(false);
      }
      return;
    }
    draftBodyRef.current = persistedDraftBody;
    draftKindRef.current = persistedDraftKind;
    setDraftBody(persistedDraftBody);
    setDraftKind(persistedDraftKind);
  }, [activeDraftId, draftSerializer, persistedDraftBody, persistedDraftKind]);

  const toggleCodeWrap = useCallback(() => {
    setWrapCode((current) => {
      const next = !current;
      localStorage.setItem(CODE_WRAP_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const toggleSplitView = useCallback(() => {
    setSplitView((current) => {
      const next = !current;
      localStorage.setItem(SPLIT_VIEW_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const lodgeComment = useCallback(async () => {
    const body = draftBody.trim();
    if (!body) return;
    draftSerializer.cancel();
    await draftSerializer.flush().catch(() => undefined);
    if (activeLineAnchor) {
      await onDraft(activeDraftId, body, activeLineAnchor, draftKind);
      draftBodyRef.current = "";
      draftDirtyRef.current = false;
      setDraftBody("");
      setDraftDirty(false);
      setActiveLineAnchor(undefined);
      return;
    }
    await onDraft(`comment:${crypto.randomUUID()}`, body);
    await onDeleteDraft(activeDraftId);
    draftBodyRef.current = "";
    draftDirtyRef.current = false;
    draftEditedRef.current = true;
    setDraftBody("");
    setDraftDirty(false);
  }, [activeDraftId, activeLineAnchor, draftBody, draftKind, draftSerializer, onDeleteDraft, onDraft]);

  useEffect(() => {
    const textarea = composerRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(128, Math.max(30, textarea.scrollHeight))}px`;
    textarea.style.overflowY = textarea.scrollHeight > 128 ? "auto" : "hidden";
  }, [draftBody]);

  // One key for every presentation mode: a per-mode key made the panels
  // silently open or close depending on how the next PR happened to score.
  useEffect(() => localStorage.setItem("chew:panel:right:v3", treeExpanded ? "expanded" : "collapsed"), [treeExpanded]);
  useEffect(() => localStorage.setItem("chew:panel:left:v3", guideExpanded ? "expanded" : "collapsed"), [guideExpanded]);
  useEffect(() => localStorage.setItem(FILE_ORDER_STORAGE_KEY, fileOrder), [fileOrder]);
  useEffect(() => setBookmarks(loadBookmarks(bookmarkKey)), [bookmarkKey]);
  useEffect(() => {
    if (guideWidth !== undefined) localStorage.setItem("chew:panel:left-width:v2", String(guideWidth));
  }, [guideWidth]);
  useEffect(() => {
    if (treeWidth !== undefined) localStorage.setItem("chew:panel:right-width:v2", String(treeWidth));
  }, [treeWidth]);

  useEffect(() => {
    if (!focus) return;
    if (focusStopStatuses[focus.stopId] === "visited" || locallyVisitedStops.has(focus.stopId)) return;
    setLocallyVisitedStops((current) => new Set(current).add(focus.stopId));
    onFocusStopVisited(focus.stopId);
  }, [focus?.stopId, focusStopStatuses, locallyVisitedStops, onFocusStopVisited]);

  const stopVisited = (stopId: string) =>
    locallyVisitedStops.has(stopId) || focusStopStatuses[stopId] === "visited";
  const regionPresented = (regionId: string) => {
    if (regionStatuses[regionId] === "presented" || locallyPresentedRegions.has(regionId)) return true;
    const stops = tourStops.filter((candidate) => candidate.regionId === regionId);
    return stops.length > 0 && stops.every((candidate) => stopVisited(candidate.stopId));
  };
  const directItems = resolvedItems.filter((candidate) => candidate.coverageMode === "direct");
  const directShown = directItems.filter((candidate) => regionPresented(candidate.regionId)).length;

  useEffect(() => {
    for (const candidate of directItems) {
      if (
        regionStatuses[candidate.regionId] !== "presented" &&
        !locallyPresentedRegions.has(candidate.regionId) &&
        regionPresented(candidate.regionId)
      ) {
        setLocallyPresentedRegions((current) => new Set(current).add(candidate.regionId));
        onRegionPresented(candidate.regionId);
      }
    }
  }, [focusStopStatuses, locallyPresentedRegions, locallyVisitedStops, onRegionPresented, regionStatuses]);

  const progressByPath = useMemo(() => {
    const scrutinyRank = { scan: 0, standard: 1, deep: 2 } as const;
    const result: Record<string, FileReviewProgress> = {};
    for (const region of compilation.regions) {
      const current = result[region.path] ?? {
        changedLoc: 0,
        natures: [],
        scrutiny: "scan" as const,
        totalStops: 0,
        visitedStops: 0,
      };
      current.changedLoc += region.changedLoc ?? 0;
      if (!current.natures.includes(region.nature)) current.natures.push(region.nature);
      if (scrutinyRank[region.scrutiny] > scrutinyRank[current.scrutiny]) current.scrutiny = region.scrutiny;
      result[region.path] = current;
    }
    for (const stop of allTourStops) {
      const current = result[stop.path];
      if (!current) continue;
      current.totalStops += 1;
      if (stopVisited(stop.stopId)) current.visitedStops += 1;
    }
    return result;
  }, [allTourStops, compilation.regions, focusStopStatuses, locallyVisitedStops]);

  const stepFileProgress = useMemo(() => stepPaths.map((path) => {
    const stops = tourStops.filter((stop) => stop.path === path);
    return {
      path,
      total: stops.length,
      visited: stops.filter((stop) => stopVisited(stop.stopId)).length,
    };
  }), [focusStopStatuses, locallyVisitedStops, stepPaths, tourStops]);
  const fullReviewProgress = useMemo(() => {
    const uniqueStops = [...new Map(allTourStops.map((stop) => [stop.stopId, stop])).values()];
    const visited = uniqueStops.filter((stop) => stopVisited(stop.stopId)).length;
    return { total: uniqueStops.length, visited, percentage: uniqueStops.length ? Math.round((visited / uniqueStops.length) * 100) : 0 };
  }, [allTourStops, focusStopStatuses, locallyVisitedStops]);
  const stepEffort = useMemo(() => {
    const scrutinyWeight = layer.scrutiny === "deep" ? 3 : layer.scrutiny === "standard" ? 1 : 0;
    const score = tourStops.length + stepPaths.length + scrutinyWeight;
    const effort = score <= 4 ? "quick" as const : score <= 10 ? "focused" as const : "heavy" as const;
    const allStops = Math.max(1, new Set(allTourStops.map((stop) => stop.stopId)).size);
    const estimatedMinutes = Math.max(1, Math.round(guide.queue.estimatedMinutes * Math.max(1, tourStops.length) / allStops));
    return { effort, estimatedMinutes };
  }, [allTourStops, guide.queue.estimatedMinutes, layer.scrutiny, stepPaths.length, tourStops.length]);
  const fileChatGuidance = useMemo(() => ({
    overview: {
      problem: guide.overview.problem,
      approach: guide.overview.approach,
      mainRisk: guide.overview.mainRisk,
      reviewFocus: guide.overview.reviewFocus,
    },
    step: {
      title: layer.title,
      narrative: layer.narrative,
      scrutiny: layer.scrutiny,
      riskTags: layer.riskTags,
    },
  }), [guide.overview, layer]);

  const stepGroups = useMemo<FileStepGroup[]>(() => guide.layers.map((guideLayer, index) => {
    const stops = guideLayer.items.flatMap((candidate) =>
      candidate.regionIds.flatMap((regionId) => buildTourStops(resolveGuideItem(guide, compilation, candidate, regionId))),
    );
    const authoredPaths = [...new Set(stops.map((candidate) => candidate.path))];
    return {
      id: guideLayer.id,
      index,
      paths: orderStepPaths(authoredPaths, compilation.regions, fileOrder),
      scrutiny: guideLayer.scrutiny,
      title: guideLayer.title,
      totalStops: stops.length,
      visitedStops: stops.filter((candidate) => stopVisited(candidate.stopId)).length,
    };
  }), [compilation, fileOrder, focusStopStatuses, guide, locallyVisitedStops]);

  const selectFocus = (
    index: number,
    intent: "reveal" | "progress-forward" | "progress-backward" = "reveal",
  ) => {
    const candidate = tourStops[index];
    if (!candidate) return;
    const previous = tourStops[stopIndex];
    scrollCursorLockedUntilRef.current = performance.now() + 400;
    setStopIndex(index);
    setSelectedPath(candidate.path);
    setRevealRequest((current) => ({ path: candidate.path, token: current.token + 1 }));
    onFocusChange(candidate.stopId, "navigation");
    setCollapsedPaths((current) => {
      const next = new Set(current);
      next.delete(candidate.path);
      if (
        intent === "progress-forward" &&
        previous &&
        previous.path !== candidate.path
      ) {
        next.add(previous.path);
      }
      return next;
    });
  };

  useEffect(() => {
    const stage = editorStageRef.current;
    if (!stage) return;
    let frame = 0;
    const updateCursorFromScroll = () => {
      scheduleViewStateSave();
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        if (performance.now() < scrollCursorLockedUntilRef.current) return;
        const bounds = stage.getBoundingClientRect();
        const rows = [...stage.querySelectorAll<HTMLElement>(".diff-row")];
        const cursorY = bounds.top + Math.min(bounds.height / 2, 260);
        const visible = rows
          .filter((row) => {
            const rowBounds = row.getBoundingClientRect();
            return rowBounds.bottom > bounds.top + 34 && rowBounds.top < bounds.bottom;
          })
          .sort((left, right) => {
            const leftBounds = left.getBoundingClientRect();
            const rightBounds = right.getBoundingClientRect();
            const leftDistance = Math.abs((leftBounds.top + leftBounds.bottom) / 2 - cursorY);
            const rightDistance = Math.abs((rightBounds.top + rightBounds.bottom) / 2 - cursorY);
            return leftDistance - rightDistance;
          })[0];
        const section = visible?.closest<HTMLElement>(".step-file");
        const path = section?.dataset.filePath;
        if (!visible || !path) return;
        const right = Number(visible.dataset.rightLine);
        const left = Number(visible.dataset.leftLine);
        const candidates = tourStops
          .map((stop, index) => ({ index, line: stop.side === "RIGHT" ? right : left, stop }))
          .filter(({ line, stop }) => stop.path === path && Number.isFinite(line) && line >= stop.startLine && line <= stop.endLine)
          .sort((a, b) => Math.abs(a.line - (a.stop.startLine + a.stop.endLine) / 2) - Math.abs(b.line - (b.stop.startLine + b.stop.endLine) / 2));
        const next = candidates[0];
        if (!next) return;
        setStopIndex((current) => {
          if (current === next.index) return current;
          onFocusChange(next.stop.stopId, "scroll");
          return next.index;
        });
        setSelectedPath((current) => current === path ? current : path);
      });
    };
    stage.addEventListener("scroll", updateCursorFromScroll, { passive: true });
    return () => {
      stage.removeEventListener("scroll", updateCursorFromScroll);
      window.cancelAnimationFrame(frame);
    };
  }, [onFocusChange, scheduleViewStateSave, tourStops]);

  const revealFileSection = useCallback((path: string) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      const section = [...document.querySelectorAll<HTMLElement>(".step-file")]
        .find((candidate) => candidate.dataset.filePath === path);
      section?.scrollIntoView({ block: "start" });
    }));
  }, []);

  const selectFile = (path: string) => {
    onNeedFile?.(path);
    const guidedIndex = tourStops.findIndex((candidate) => candidate.path === path);
    if (guidedIndex >= 0) {
      scrollCursorLockedUntilRef.current = performance.now() + 400;
      setStopIndex(guidedIndex);
      setRevealRequest((current) => ({ path, token: current.token + 1 }));
      onFocusChange(tourStops[guidedIndex]!.stopId, "navigation");
    }
    setSelectedPath(path);
    setCollapsedPaths((current) => {
      if (!current.has(path)) return current;
      const next = new Set(current);
      next.delete(path);
      return next;
    });
    if (guidedIndex < 0) revealFileSection(path);
  };

  const updateBookmarks = useCallback((update: (current: ReviewBookmark[]) => ReviewBookmark[]) => {
    setBookmarks((current) => {
      const next = update(current);
      saveBookmarks(bookmarkKey, next);
      return next;
    });
  }, [bookmarkKey]);

  const revealCodeLocation = useCallback((path: string, side: "LEFT" | "RIGHT", line: number) => {
    onNeedFile?.(path);
    const nearest = tourStops
      .map((stop, index) => ({
        index,
        stop,
        distance: stop.path !== path ? Number.MAX_SAFE_INTEGER : line < stop.startLine ? stop.startLine - line : line > stop.endLine ? line - stop.endLine : 0,
      }))
      .filter((candidate) => candidate.stop.path === path)
      .sort((left, right) => left.distance - right.distance || left.index - right.index)[0];
    scrollCursorLockedUntilRef.current = performance.now() + 700;
    if (nearest) {
      setStopIndex(nearest.index);
      onFocusChange(nearest.stop.stopId, "navigation");
    }
    setSelectedPath(path);
    setCollapsedPaths((current) => {
      if (!current.has(path)) return current;
      const next = new Set(current);
      next.delete(path);
      return next;
    });
    setLineRevealRequest((current) => ({ path, side, line, token: current.token + 1 }));
  }, [onFocusChange, onNeedFile, tourStops]);

  const completeLineReveal = useCallback((token: number) => {
    setLineRevealRequest((current) => current.token === token ? { ...current, path: "" } : current);
  }, []);

  const toggleBookmark = useCallback((anchor: DraftAnchor, lineText: string) => {
    const id = bookmarkId(anchor.path, anchor.side, anchor.line);
    updateBookmarks((current) => {
      if (current.some((bookmark) => bookmark.id === id)) {
        return current.filter((bookmark) => bookmark.id !== id);
      }
      return [...current, {
        id,
        repository: descriptor.repository,
        pullNumber: descriptor.pullNumber,
        headSha: guide.source.headSha,
        path: anchor.path,
        side: anchor.side,
        line: anchor.line,
        lineText,
        createdAt: new Date().toISOString(),
      }];
    });
  }, [descriptor.pullNumber, descriptor.repository, guide.source.headSha, updateBookmarks]);

  const removeBookmark = useCallback((bookmark: ReviewBookmark) => {
    updateBookmarks((current) => current.filter((candidate) => candidate.id !== bookmark.id));
  }, [updateBookmarks]);

  const markBookmarkReviewed = useCallback((bookmark: ReviewBookmark) => {
    const currentText = lineTextAt(files.files[bookmark.path], bookmark.side, bookmark.line);
    if (currentText === undefined) return;
    updateBookmarks((current) => current.map((candidate) => candidate.id === bookmark.id ? {
      ...candidate,
      headSha: guide.source.headSha,
      lineText: currentText,
    } : candidate));
  }, [files.files, guide.source.headSha, updateBookmarks]);

  const selectSearchResult = useCallback((result: ExactSearchResult) => {
    setSearchOpen(false);
    revealCodeLocation(result.path, result.side, result.line);
  }, [revealCodeLocation]);

  const selectBookmark = useCallback((bookmark: ReviewBookmark) => {
    setBookmarksOpen(false);
    revealCodeLocation(bookmark.path, bookmark.side, bookmark.line);
  }, [revealCodeLocation]);

  const selectConversationLocation = (path: string, line?: number, side: "LEFT" | "RIGHT" = "RIGHT") => {
    if (line !== undefined) revealCodeLocation(path, side, line);
    else selectFile(path);
  };

  const toggleFile = useCallback((path: string) => {
    setCollapsedPaths((current) => {
      const next = new Set(current);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const viewedStateByPath = useMemo(() => {
    const result = new Map<string, ViewedFileState>();
    for (const [path, mark] of Object.entries(viewedMarks)) {
      const file = files.files[path];
      const fingerprint = file && !file.loading && !file.binary ? fileContentFingerprint(file) : undefined;
      const state = viewedFileState(mark, fingerprint);
      if (state) result.set(path, state);
    }
    return result;
  }, [files.files, viewedMarks]);

  const viewedTreePaths = useMemo(
    () => new Set([...viewedStateByPath.entries()].filter(([, state]) => state === "viewed").map(([path]) => path)),
    [viewedStateByPath],
  );

  const toggleFileViewed = useCallback((path: string) => {
    // A file whose diff changed since it was marked re-marks against the new
    // content instead of clearing, matching what the click is asking for.
    const alreadyViewed = viewedStateByPath.get(path) === "viewed";
    const next = { ...viewedMarks };
    if (alreadyViewed) delete next[path];
    else {
      const file = files.files[path];
      next[path] = {
        fingerprint: file && !file.loading && !file.binary ? fileContentFingerprint(file) : "",
        markedAt: new Date().toISOString(),
      };
    }
    saveViewedFiles(viewedKey, next);
    setViewedMarks(next);
    setCollapsedPaths((collapsed) => {
      const nextCollapsed = new Set(collapsed);
      if (alreadyViewed) nextCollapsed.delete(path);
      else nextCollapsed.add(path);
      return nextCollapsed;
    });
  }, [files.files, viewedKey, viewedMarks, viewedStateByPath]);

  const beginResize = (panel: "guide" | "tree", event: ReactPointerEvent<HTMLDivElement>) => {
    if (window.innerWidth <= 1140) return;
    event.preventDefault();
    const workspace = event.currentTarget.closest<HTMLElement>(".review-workspace");
    if (!workspace) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    document.body.classList.add("resizing-panels");
    const onMove = (moveEvent: PointerEvent) => {
      const bounds = workspace.getBoundingClientRect();
      const otherPanel = workspace.querySelector<HTMLElement>(panel === "guide" ? ".file-tree" : ".guide-sidebar");
      const otherWidth = otherPanel?.getBoundingClientRect().width ?? responsivePanelWidth(panel === "guide" ? "tree" : "guide");
      const maximum = Math.max(220, Math.min(480, bounds.width - otherWidth - 452));
      if (panel === "guide") setGuideWidth(Math.max(220, Math.min(maximum, moveEvent.clientX - bounds.left - 8)));
      else setTreeWidth(Math.max(220, Math.min(maximum, bounds.right - moveEvent.clientX - 8)));
    };
    const onUp = () => {
      document.body.classList.remove("resizing-panels");
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
  };

  const continueTour = () => {
    onStatus("done");
    onNext();
  };

  const previousFocus = () => {
    if (stopIndex > 0) selectFocus(stopIndex - 1, "progress-backward");
    else if (stepNumber === 1) setExitTarget("briefing");
    else onPrevious();
  };

  const nextFocus = () => {
    if (stopIndex < tourStops.length - 1) selectFocus(stopIndex + 1, "progress-forward");
    else continueTour();
  };

  const confirmReviewExit = () => {
    setExitTarget(undefined);
    onPrevious();
  };

  const returnToFocus = () => {
    setFullDiffOpen(false);
    if (fullDiffOpen) setGuideExpanded(true);
    if (!focus) selectFocus(stopIndex);
    else {
      scrollCursorLockedUntilRef.current = performance.now() + 400;
      setRevealRequest((current) => ({ path: focus.path, token: current.token + 1 }));
    }
  };


  // Whole-diff mode renders every path, but the workspace only preloads the
  // first two guide layers. Ask for whatever is still loading whenever that set
  // changes, not just on the way in: it is the landing mode now, and the paths
  // are not all known on the first render.
  useEffect(() => {
    if (following) return;
    const wanted = hiddenPaths.filter((path) => files.files[path]?.loading);
    if (wanted.length > 0) onNeedFiles?.(wanted);
  }, [files, following, hiddenPaths, onNeedFiles]);

  // Panes follow the mode, but only once the reviewer actually switches, so a
  // restored panel preference survives the initial render.
  const followedRef = useRef(following);
  useEffect(() => {
    if (followedRef.current === following) return;
    followedRef.current = following;
    if (following) {
      setGuideExpanded(true);
      if (!focus) selectFocus(stopIndex);
    } else {
      setGuideExpanded(false);
      setTreeExpanded(true);
    }
  });

  const moveFile = (delta: number) => {
    const navigablePaths = displayedPaths.length > 0 ? displayedPaths : filePaths;
    const currentIndex = Math.max(0, navigablePaths.indexOf(selectedPath));
    const nextPath = navigablePaths[Math.max(0, Math.min(navigablePaths.length - 1, currentIndex + delta))];
    if (nextPath) selectFile(nextPath);
  };

  const focusFileSearch = () => {
    setTreeExpanded(true);
    window.requestAnimationFrame(() => document.querySelector<HTMLInputElement>(".file-tree input")?.focus());
  };

  const focusComment = () => {
    window.requestAnimationFrame(() => document.querySelector<HTMLTextAreaElement>(".review-composer textarea")?.focus());
  };

  const selectLineComment = useCallback((anchor: DraftAnchor) => {
    setActiveLineAnchor(anchor);
    setSelectedPath(anchor.path);
    setCollapsedPaths((current) => {
      if (!current.has(anchor.path)) return current;
      const next = new Set(current);
      next.delete(anchor.path);
      return next;
    });
    window.requestAnimationFrame(() => composerRef.current?.focus());
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const textEntry = target?.closest<HTMLElement>("input, textarea, select, [contenteditable='true']");
      const isTyping = Boolean(textEntry);
      if (document.querySelector('[data-slot="dialog-content"][data-open], [data-slot="popover-content"][data-open]')) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen((open) => !open);
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "f") {
        if (isTyping) return;
        event.preventDefault();
        setSearchOpen(true);
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key === "ArrowRight") {
        if (isTyping) return;
        event.preventDefault();
        onOpenReceipt();
        return;
      }
      if (event.altKey && event.key.toLowerCase() === "z") {
        if (isTyping) return;
        event.preventDefault();
        toggleCodeWrap();
        return;
      }
      if (event.altKey && event.key.toLowerCase() === "s") {
        if (isTyping) return;
        event.preventDefault();
        toggleSplitView();
        return;
      }
      if (event.key === "?") {
        if (isTyping) return;
        event.preventDefault();
        setShortcutsOpen((open) => !open);
        return;
      }
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key === "Escape" && (shortcutsOpen || commandOpen)) {
        event.preventDefault();
        setShortcutsOpen(false);
        setCommandOpen(false);
        return;
      }
      if (event.key === "Escape" && textEntry) {
        event.preventDefault();
        textEntry.blur();
        return;
      }
      if (shortcutsOpen || commandOpen || isTyping) return;
      if (
        (event.key === " " || event.code === "Space") &&
        !target?.closest("button, a, input, textarea, select, [contenteditable='true']")
      ) {
        const path = focus?.path ?? selectedPath;
        if (!path) return;
        event.preventDefault();
        toggleFile(path);
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        previousFocus();
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        nextFocus();
      }
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        editorStageRef.current?.scrollBy({
          top: event.key === "ArrowUp" ? -REVIEW_SCROLL_STEP_PX : REVIEW_SCROLL_STEP_PX,
          behavior: "auto",
        });
      }
      if (event.key === "[") {
        event.preventDefault();
        setGuideExpanded((expanded) => !expanded);
      }
      if (event.key === "]") {
        event.preventDefault();
        setTreeExpanded((expanded) => !expanded);
      }
      if (event.key === "/") {
        event.preventDefault();
        focusFileSearch();
      }
      if (event.key.toLowerCase() === "j") {
        event.preventDefault();
        moveFile(1);
      }
      if (event.key.toLowerCase() === "k") {
        event.preventDefault();
        moveFile(-1);
      }
      if (event.key.toLowerCase() === "c") {
        event.preventDefault();
        focusComment();
      }
      if (event.key.toLowerCase() === "r") {
        event.preventDefault();
        returnToFocus();
      }
      if (event.key.toLowerCase() === "o") {
        event.preventDefault();
        onOpenOverview();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [commandOpen, focus, onOpenOverview, onOpenReceipt, selectedPath, shortcutsOpen, stopIndex, toggleCodeWrap, toggleSplitView, toggleFile, tourStops.length]);

  const commands = [
    { label: guideExpanded ? (unguided ? "Collapse context" : "Collapse guide") : (unguided ? "Expand context" : "Expand guide"), hint: "[", run: () => setGuideExpanded((expanded) => !expanded) },
    { label: treeExpanded ? "Collapse file picker" : "Expand file picker", hint: "]", run: () => setTreeExpanded((expanded) => !expanded) },
    { label: "Find changed file", hint: "/", run: focusFileSearch },
    { label: "Search changed code", hint: "⌘F", run: () => setSearchOpen(true) },
    { label: wrapCode ? "Disable code wrapping" : "Wrap long code", hint: "⌥Z", run: toggleCodeWrap },
    { label: splitView ? "Unified file view" : "Split file view", hint: "⌥S", run: toggleSplitView },
    { label: "Open bookmarks", hint: "", run: () => setBookmarksOpen(true) },
    { label: "Return to guided focus", hint: "R", run: returnToFocus },
    { label: collapsedPaths.has(focus?.path ?? selectedPath) ? "Expand current file" : "Collapse current file", hint: "Space", run: () => {
      const path = focus?.path ?? selectedPath;
      if (path) toggleFile(path);
    } },
    { label: "Open review report", hint: "⌘→", run: onOpenReceipt },
    { label: "Comment on current focus", hint: "C", run: focusComment },
    { label: "Previous file", hint: "K", run: () => moveFile(-1) },
    { label: "Next file", hint: "J", run: () => moveFile(1) },
    { label: "Open PR overview", hint: "O", run: onOpenOverview },
    { label: codeCommentMode === "show" ? "Extract code comments" : "Show code comments inline", hint: "", run: () => setCodeCommentMode((mode) => {
      const next = mode === "show" ? "extract" : "show";
      localStorage.setItem(CODE_COMMENT_STORAGE_KEY, next);
      return next;
    }) },
    ...fileOrderOptions.map((option) => ({ label: `Order files: ${option.label}`, hint: fileOrder === option.mode ? "✓" : "", run: () => setFileOrder(option.mode) })),
  ];

  const runCommand = (run: () => void) => {
    setCommandOpen(false);
    run();
  };

  return (
    <article
      className={`review-workspace review-${reviewMode}${guideExpanded ? "" : " guide-collapsed"}${treeExpanded ? "" : " files-collapsed"}`}
      style={{
        "--guide-width": guideWidth === undefined ? reviewMode === "fast-lane" ? "clamp(250px, 18vw, 310px)" : "clamp(280px, 23vw, 370px)" : `${guideWidth}px`,
        "--files-width": treeWidth === undefined ? "clamp(240px, 19vw, 300px)" : `${treeWidth}px`,
      } as CSSProperties}
    >
      <section className={`code-workspace${treeExpanded ? "" : " tree-collapsed"}`} aria-label="Changed code">
        <FileTree
          changedSincePaths={changedSincePaths}
          collapsed={!treeExpanded}
          conversation={pullContext?.conversation}
          conversationDraft={conversationDraft}
          files={files}
          focusedPaths={focusedPaths}
          onConversationDraft={(body) => onDraft("conversation:new", body)}
          onReplyThread={onReplyThread}
          onSelect={selectFile}
          onSelectConversation={selectConversationLocation}
          onToggle={() => setTreeExpanded((expanded) => !expanded)}
          progressByPath={progressByPath}
          selectedPath={selectedPath}
          stepGroups={stepGroups}
          currentStepId={layer.id}
          viewedPaths={viewedTreePaths}
        />
        <div className="editor-panel">
          <div className="file-context-bar" aria-label="Files outside the current step">
            {!unguided && <HiddenFilePicker hiddenPaths={hiddenPaths} files={files} onSelect={selectFile} />}
            <div className="file-context-actions">
              <FileOrderPicker mode={fileOrder} onChange={setFileOrder} />
              <CodeSearchPopover
                loadingPaths={loadingPaths}
                onLoadAll={() => onNeedFiles?.(loadingPaths)}
                onOpenChange={setSearchOpen}
                onQueryChange={setSearchQuery}
                onSelect={selectSearchResult}
                open={searchOpen}
                query={searchQuery}
                results={searchResults}
              />
              <BookmarksPopover
                bookmarks={bookmarks}
                files={files}
                headSha={guide.source.headSha}
                onMarkReviewed={markBookmarkReviewed}
                onOpenChange={setBookmarksOpen}
                onRemove={removeBookmark}
                onSelect={selectBookmark}
                open={bookmarksOpen}
              />
              <CodeCommentPicker mode={codeCommentMode} onChange={(mode) => {
                setCodeCommentMode(mode);
                localStorage.setItem(CODE_COMMENT_STORAGE_KEY, mode);
              }} />
              <ToolButton label={wrapCode ? "Disable code wrapping" : "Wrap long code"} onClick={toggleCodeWrap} pressed={wrapCode} shortcut="⌥Z"><WrapText /></ToolButton>
              <ToolButton label={splitView ? "Unified file view" : "Split file view"} onClick={toggleSplitView} pressed={splitView} shortcut="⌥S"><Columns2 /></ToolButton>
              {!focus && !fullDiffOpen && <ToolButton label="Return to guided focus" onClick={returnToFocus} shortcut="R"><LocateFixed /></ToolButton>}
              <ToolButton label="Open commands" onClick={() => setCommandOpen(true)} shortcut="⌘K"><CommandIcon /></ToolButton>
              <ToolButton label="Keyboard shortcuts" onClick={() => setShortcutsOpen(true)} shortcut="?"><CircleHelp /></ToolButton>
            </div>
          </div>
          <div className="editor-stage" aria-label={fullDiffOpen ? `${displayedPaths.length} changed files in full diff` : `${displayedPaths.length} changed files in this review step`} ref={editorStageRef}>
            <div className="step-files">
              {displayedPaths.map((path) => (
                <StepFileDiff
                  activeAnchor={activeLineAnchor?.path === path ? activeLineAnchor : undefined}
                  annotations={annotationsByPath.get(path) ?? EMPTY_ANNOTATIONS}
                  bookmarks={bookmarksByPath.get(path) ?? EMPTY_LINE_BOOKMARKS}
                  collapsed={collapsedPaths.has(path)}
                  comments={lineCommentsByPath.get(path) ?? EMPTY_LINE_COMMENTS}
                  codeCommentMode={codeCommentMode}
                  descriptor={descriptor}
                  file={files.files[path]}
                  files={files}
                  focus={focus?.path === path ? focus : undefined}
                  key={path}
                  onSelectComment={selectLineComment}
                  onToggleBookmark={toggleBookmark}
                  onToggleFile={toggleFile}
                  onToggleViewed={toggleFileViewed}
                  path={path}
                  pullContext={pullContext}
                  pullDetails={pullDetails}
                  reviewGuidance={fileChatGuidance}
                  regions={regionsByPath.get(path) ?? EMPTY_REGIONS}
                  revealLine={lineRevealRequest.path === path ? lineRevealRequest : undefined}
                  onLineRevealed={completeLineReveal}
                  revealToken={revealRequest.path === path ? revealRequest.token : 0}
                  reviewThreads={reviewThreadsByPath.get(path) ?? []}
                  codePaths={filePaths}
                  onReplyThread={onReplyThread}
                  onSelectLocation={selectConversationLocation}
                  onPreviewCode={setCodePreviewTarget}
                  selected={selectedPath === path}
                  viewedState={viewedStateByPath.get(path)}
                  wrapCode={wrapCode}
                  splitView={splitView}
                />
              ))}
            </div>
          </div>
          <div className="floating-review-toolbar" aria-label="Review controls">
            {activeLineAnchor && (
              <div className="composer-line-toolbar">
                <span className="composer-line-target"><MessageSquare aria-hidden="true" /><strong>{pathParts(activeLineAnchor.path).fileName}</strong><small>{anchorLabel(activeLineAnchor)}</small></span>
                <div aria-label="Comment type" className="composer-comment-kinds" role="radiogroup">
                  {draftCommentKinds.map((kind) => (
                    <button
                      aria-checked={draftKind === kind}
                      className={`comment-kind-${kind}`}
                      key={kind}
                      onClick={() => {
                        draftKindRef.current = kind;
                        draftDirtyRef.current = true;
                        draftEditedRef.current = true;
                        setDraftKind(kind);
                        setDraftDirty(true);
                        draftSerializer.schedule({ itemId: activeDraftId, body: draftBodyRef.current, anchor: activeLineAnchor, kind });
                      }}
                      role="radio"
                      type="button"
                    >{commentKindLabels[kind]}</button>
                  ))}
                </div>
                {draftBody && <button aria-label={activeLineAnchor && anchorSpansRange(activeLineAnchor) ? "Delete range comment" : "Delete line comment"} className="composer-icon-button delete" onClick={() => {
                  draftSerializer.cancel();
                  draftBodyRef.current = "";
                  draftDirtyRef.current = false;
                  draftEditedRef.current = true;
                  setDraftBody("");
                  setDraftDirty(false);
                  setActiveLineAnchor(undefined);
                  void draftSerializer.flush().catch(() => undefined).then(() => onDeleteDraft(activeDraftId));
                }} title="Delete comment" type="button"><Trash2 aria-hidden="true" /></button>}
                <button aria-label={activeLineAnchor && anchorSpansRange(activeLineAnchor) ? "Stop commenting on these lines" : "Stop commenting on this line"} className="composer-icon-button" onClick={() => setActiveLineAnchor(undefined)} title="Back to review comments" type="button"><X aria-hidden="true" /></button>
              </div>
            )}
            <div className="floating-review-main">
              <div className={`review-composer${activeLineAnchor ? " line-comment-active" : ""}`}>
                <div className="review-composer-input">
                <textarea
                  aria-label={activeLineAnchor ? `Comment on ${anchorLabel(activeLineAnchor)} in ${pathParts(activeLineAnchor.path).fileName}` : "Draft review comment"}
                  onChange={(event) => {
                    const nextBody = event.target.value;
                    draftBodyRef.current = nextBody;
                    draftDirtyRef.current = true;
                    draftEditedRef.current = true;
                    setDraftBody(nextBody);
                    setDraftDirty(true);
                    event.target.style.height = "auto";
                    event.target.style.height = `${Math.min(128, Math.max(30, event.target.scrollHeight))}px`;
                    event.target.style.overflowY = event.target.scrollHeight > 128 ? "auto" : "hidden";
                    draftSerializer.schedule({
                      itemId: activeDraftId,
                      body: nextBody,
                      anchor: activeLineAnchor,
                      kind: activeLineAnchor ? draftKindRef.current : undefined,
                    });
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
                    event.preventDefault();
                    void lodgeComment();
                  }}
                  placeholder={activeLineAnchor ? anchorSpansRange(activeLineAnchor) ? "Comment on these lines…" : "Comment on this line…" : "Add a review comment…"}
                  ref={composerRef}
                  rows={1}
                  value={draftBody}
                />
                <span>{activeLineAnchor
                  ? draftDirty ? "Saving…" : draftBody ? "Saved" : anchorSpansRange(activeLineAnchor) ? "Range comment" : "Line comment"
                  : `${focus ? "Step comment" : "File comment"}${draftBody ? draftDirty ? " · Saving…" : " · Saved" : ""}`}</span>
                </div>
              </div>
              {toolbarAction && <div className="floating-review-outcome">{toolbarAction}</div>}
              <div className="floating-review-navigation">
                <ToolButton label="Previous focus" onClick={previousFocus} shortcut="←"><ChevronLeft /></ToolButton>
                <ToolButton label="Next focus" onClick={nextFocus} shortcut="→"><ChevronRight /></ToolButton>
              </div>
              <div className="floating-review-progress" aria-label="Full review progress">
                <button aria-label={`Full review ${fullReviewProgress.percentage}% complete`} className="full-review-progress" onClick={onOpenReceipt} style={{ "--review-progress": `${fullReviewProgress.percentage * 3.6}deg` } as CSSProperties} title={`${fullReviewProgress.visited}/${fullReviewProgress.total} focal stops · Open review report`} type="button"><span>{fullReviewProgress.percentage}%</span></button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <aside className={`guide-sidebar guide-sidebar-${sidebarMode}${guideExpanded ? "" : " guide-sidebar-collapsed"}`} aria-label="Review context">
        <button aria-label={unguided ? "Expand review context" : "Expand guide"} className="guide-rail" onClick={() => setGuideExpanded(true)} title={unguided ? "Expand review context · [" : "Expand guide · ["} type="button">
          <span>{sidebarMode === "guide" ? "Guide" : "PR"}</span><small>{sidebarMode === "guide" ? `${String(stepNumber).padStart(2, "0")}/${String(totalSteps).padStart(2, "0")}` : `#${descriptor.pullNumber}`}</small><kbd>[</kbd>
        </button>
        <div className="guide-sidebar-toolbar">
          <span className="guide-sidebar-title">{sidebarMode === "guide" ? "Guide" : "PR info"}</span>
          <ToolButton label={unguided ? "Collapse review context" : "Collapse guide"} onClick={() => setGuideExpanded(false)} shortcut="["><PanelLeftClose /></ToolButton>
        </div>
        {sidebarMode === "guide" && <>
          <StepNavigator currentStepId={layer.id} effort={stepEffort.effort} estimatedMinutes={stepEffort.estimatedMinutes} fileCount={stepPaths.length} focusCount={tourStops.length} onSelect={onSelectStep} steps={reviewSteps} />
          <section className="guide-step-progress" aria-label="Current step progress">
            <header>
              <span>Step progress</span>
              <strong>{stepFileProgress.reduce((total, file) => total + file.visited, 0)}/{stepFileProgress.reduce((total, file) => total + file.total, 0)} focus points</strong>
            </header>
            <div className="step-file-progress" aria-label={`${stepFileProgress.filter((file) => file.total > 0 && file.visited >= file.total).length} of ${stepFileProgress.length} files complete in this step`}>
              {stepFileProgress.map((file) => {
                const percentage = file.total ? Math.round((file.visited / file.total) * 100) : 0;
                return <button aria-label={`${pathParts(file.path).fileName}: ${file.visited} of ${file.total} focal stops`} key={file.path} onClick={() => selectFile(file.path)} style={{ flexGrow: Math.max(1, file.total), "--segment-progress": `${percentage}%` } as CSSProperties} title={`${pathParts(file.path).fileName} · ${percentage}%`} type="button" />;
              })}
            </div>
          </section>
        </>}
        <div className="guide-sidebar-scroll">
          {sidebarMode === "pr" ? (
            <PullInfoPanel descriptor={descriptor} guide={guide} onOpenOverview={onOpenOverview} onPreviewCode={setCodePreviewTarget} pullContext={pullContext} pullDetails={pullDetails} />
          ) : <>
          <header className="guide-step-header">
            <div className="layer-meta">
              {!['unseen', 'in-progress'].includes(status) && <span className="step-status">visited</span>}
            </div>
            <h1>{layer.title}</h1>
            <p><GuideProse codePaths={filePaths} onSelectCode={setCodePreviewTarget} repository={descriptor.repository} text={layer.narrative} /></p>
            {layer.riskTags.length > 0 && (
              <div className="risk-tags">
                {layer.riskTags.map((tag) => <span key={tag}>{tag.replaceAll("-", " ")}</span>)}
              </div>
            )}
          </header>

          {layer.presentation === "shape-sheet" && layer.shapes.length > 0 && (
            <details className="sidebar-supporting-view">
              <summary>Shape summary</summary>
              <ul className="shape-signatures">
                {layer.shapes.map((shape) => (
                  <li key={shape.name}>
                    <div><code>{shape.name}</code><span className={`change-${shape.change}`}>{shape.change}</span></div>
                    <code className="shape-signature">{shape.name} &#123; {shape.fields.join(", ")} &#125;</code>
                    {shape.usedBy.length > 0 && <small>Used by {shape.usedBy.join(" · ")}</small>}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <LayerPresentation layer={layer} />

          {!(reviewMode === "fast-lane" && resolvedItems.length === 1) && <section className="guide-regions" aria-labelledby="guide-regions-title">
            <header>
              <div>
                <h2 id="guide-regions-title">Regions</h2>
              </div>
              {directItems.length > 0 && <span>{directShown}/{directItems.length} shown</span>}
            </header>
            <div className="guide-region-list">
              {orderedResolvedItems.map((candidate) => {
                const candidateStops = tourStops.filter((stop) => stop.regionId === candidate.regionId);
                const visitedStops = candidateStops.filter((stop) => stopVisited(stop.stopId)).length;
                const firstStopIndex = tourStops.findIndex((stop) => stop.regionId === candidate.regionId);
                return (
                <button
                  aria-current={focus?.regionId === candidate.regionId ? "true" : undefined}
                  className={focus?.regionId === candidate.regionId ? "active" : ""}
                  key={candidate.id}
                  onClick={() => selectFocus(firstStopIndex)}
                  type="button"
                >
                  <span>{regionPresented(candidate.regionId) ? "✓" : visitedStops > 0 ? "◐" : firstStopIndex + 1}</span>
                  <div>
                    <strong>{candidate.path.split("/").at(-1)}</strong>
                    <small>
                      {candidateStops.length > 1
                        ? `${visitedStops}/${candidateStops.length} focal stops`
                        : candidate.scope === "range"
                          ? lineRangeLabel(candidate.side, candidate.startLine, candidate.endLine)
                          : `${candidate.changedLoc} changed LOC`}
                    </small>
                  </div>
                </button>
                );
              })}
            </div>
          </section>}

          {prompts.length > 0 && (
            <details className="review-prompts">
              <summary>
                <span>Possible concerns</span>
                <small>{prompts.length}</small>
                <span
                  aria-label="Suggestions only. These concerns are incomplete and resolving them does not establish that the change is correct."
                  className="prompt-help"
                  role="img"
                  tabIndex={0}
                  title="Suggestions only. These concerns are incomplete and resolving them does not establish that the change is correct."
                >?</span>
              </summary>
              <ul>{prompts.map((prompt) => (
                <li key={prompt.id}>
                  <span>{prompt.kind.replace("-", " ")}</span>
                  <GuideProse codePaths={filePaths} onSelectCode={setCodePreviewTarget} repository={descriptor.repository} text={prompt.text} />
                </li>
              ))}</ul>
            </details>
          )}

          <details className="classification-feedback sidebar-feedback">
            <summary>Is the guide focusing on the wrong thing?</summary>
            <div>
              <textarea
                onChange={(event) => setFeedback(event.target.value)}
                placeholder="This needs different context or more scrutiny…"
                value={feedback}
              />
              <button
                disabled={!feedback.trim()}
                onClick={() => {
                  onFeedback(focus?.id, feedback.trim());
                  setFeedback("");
                }}
                type="button"
              >
                Save feedback locally
              </button>
            </div>
          </details>
          </>}
        </div>

      </aside>
      <div
        aria-label={unguided ? "Resize review context" : "Resize guide panel"}
        aria-orientation="vertical"
        aria-valuemax={480}
        aria-valuemin={220}
        aria-valuenow={guideWidth ?? responsivePanelWidth("guide")}
        className="panel-resizer panel-resizer-guide"
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.preventDefault();
            event.stopPropagation();
            setGuideWidth((width) => Math.max(220, Math.min(480, (width ?? responsivePanelWidth("guide")) + (event.key === "ArrowLeft" ? -16 : 16))));
          }
        }}
        onPointerDown={(event) => beginResize("guide", event)}
        role="separator"
        tabIndex={guideExpanded ? 0 : -1}
      />
      <div
        aria-label="Resize file panel"
        aria-orientation="vertical"
        aria-valuemax={480}
        aria-valuemin={220}
        aria-valuenow={treeWidth ?? responsivePanelWidth("tree")}
        className="panel-resizer panel-resizer-files"
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.preventDefault();
            event.stopPropagation();
            setTreeWidth((width) => Math.max(220, Math.min(480, (width ?? responsivePanelWidth("tree")) + (event.key === "ArrowLeft" ? 16 : -16))));
          }
        }}
        onPointerDown={(event) => beginResize("tree", event)}
        role="separator"
        tabIndex={treeExpanded ? 0 : -1}
      />
      <CodeReferencePreview
        file={codePreviewTarget ? files.files[codePreviewTarget.path] : undefined}
        onClose={() => setCodePreviewTarget(undefined)}
        onOpenInReview={(target) => onSelectCode?.(target)}
        target={codePreviewTarget}
      />
      <Dialog onOpenChange={(open) => { if (!open) setExitTarget(undefined); }} open={Boolean(exitTarget)}>
        <DialogContent className="review-exit-dialog max-w-[390px] gap-0 overflow-hidden p-0">
          <header>
            <DialogTitle>Leave the code review?</DialogTitle>
            <DialogDescription>
              {unguided
                ? "You are at the start of the review. Your progress is saved; this leaves the review."
                : "You are at the start of the review. Your progress is saved; this returns to the briefing."}
            </DialogDescription>
          </header>
          <footer>
            <Button onClick={() => setExitTarget(undefined)} size="sm" type="button" variant="ghost">Stay in review</Button>
            <Button onClick={confirmReviewExit} size="sm" type="button">
              {unguided ? "Leave review" : "Open briefing"}
            </Button>
          </footer>
        </DialogContent>
      </Dialog>
      <Dialog onOpenChange={setShortcutsOpen} open={shortcutsOpen}>
        <DialogContent className="shortcut-panel max-w-[420px] gap-0 overflow-hidden p-0">
            <header><DialogTitle>Keyboard</DialogTitle><DialogDescription className="sr-only">Keyboard controls for the current review</DialogDescription></header>
            <dl>
              <div><dt><kbd>←</kbd> <kbd>→</kbd></dt><dd>Previous / next focus</dd></div>
              <div><dt><kbd>↑</kbd> <kbd>↓</kbd></dt><dd>Scroll six code lines</dd></div>
              <div><dt><kbd>Space</kbd></dt><dd>Collapse / expand current file</dd></div>
              <div><dt><kbd>⌘</kbd><kbd>→</kbd></dt><dd>Open review report</dd></div>
              <div><dt><kbd>[</kbd> <kbd>]</kbd></dt><dd>Review context / file picker</dd></div>
              <div><dt><kbd>K</kbd> <kbd>J</kbd></dt><dd>Previous / next file</dd></div>
              <div><dt><kbd>/</kbd></dt><dd>Find a changed file</dd></div>
              <div><dt><kbd>⌘F</kbd></dt><dd>Search changed code</dd></div>
              <div><dt><kbd>⌥Z</kbd></dt><dd>Toggle code wrapping</dd></div>
              <div><dt><kbd>⌥S</kbd></dt><dd>Toggle split file view</dd></div>
              <div><dt><kbd>1</kbd>–<kbd>5</kbd></dt><dd>File filters</dd></div>
              {!unguided && <div><dt><kbd>R</kbd></dt><dd>Return to guided focus</dd></div>}
              <div><dt><kbd>C</kbd></dt><dd>Comment on current focus</dd></div>
              <div><dt><kbd>O</kbd></dt><dd>PR overview</dd></div>
              <div><dt><kbd>⌘K</kbd></dt><dd>Command menu</dd></div>
              <div><dt><kbd>?</kbd></dt><dd>Toggle shortcuts</dd></div>
            </dl>
        </DialogContent>
      </Dialog>
      <CommandDialog description="Navigate and control the current review" onOpenChange={setCommandOpen} open={commandOpen} title="CHEW commands">
        <CommandInput placeholder="Search review commands…" />
        <CommandList>
          <CommandEmpty>No matching command</CommandEmpty>
          <CommandGroup heading="Review">
            {commands.map((command) => (
              <CommandItem key={command.label} onSelect={() => runCommand(command.run)} value={command.label}>
                <span>{command.label}</span><CommandShortcut>{command.hint}</CommandShortcut>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </article>
  );
}
